// src/layouts/AppLayout.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Outlet } from "react-router-dom";
// Removed AuthProvider and ThemeProvider imports - they are now in App.jsx

// Keep Providers specific to the logged-in application state
import { SettingsProvider } from "../contexts/SettingsContext"; // Adjust path if needed
import { VersionProvider } from "../contexts/VersionProvider"; // Adjust path if needed

// Keep Error Handling and Loading Indicator components
import ErrorBoundary from "../components/ErrorBoundary"; // Adjust path if needed
import AppLoadingIndicator from "../components/AppLoadingIndicator"; // Adjust path if needed
import loadingManager from "../utils/AppLoadingManager"; // Adjust path if needed

// --- Define or Import Error Fallback ---
// (Assuming it's defined here for simplicity, move to separate file if preferred)
function ErrorFallback({ error }) {
  // Basic error display, can be enhanced
  return (
    <div className="p-4 text-center">
      <h1 className="text-xl font-bold text-danger">Application Error</h1>
      <p className="mt-2">
        Something went wrong loading this part of the application. Please try
        refreshing.
      </p>
      <details className="mt-3 text-start bg-light p-2 rounded border">
        <summary className="text-muted">Error Details</summary>
        <pre
          className="mt-2"
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
        >
          {error.message}
        </pre>
        {error.stack && (
          <pre className="mt-1" style={{ fontSize: "0.8em", color: "#6c757d" }}>
            {error.stack}
          </pre>
        )}
      </details>
    </div>
  );
}

// --- Define or Import requiresAppLoading ---
// Must be consistent with the one in main.jsx
// (Assuming it's defined here for simplicity, move to separate file if preferred)
const requiresAppLoading = (pathname) => {
  const publicPaths = [
    "/",
    "/login",
    "/plans",
    "/request-reset",
    "/reset-password",
  ];
  // Returns true if the initial pathname is NOT one of the public paths.
  return !publicPaths.some(
    (publicPath) =>
      pathname === publicPath ||
      (publicPath !== "/" && pathname.startsWith(publicPath + "/"))
  );
};

const AppLayout = () => {
  // --- Loading Logic START (Checks if initial load was for an app route) ---
  const needsLoading = useMemo(
    () => requiresAppLoading(window.location.pathname),
    []
  );

  const [isLoading, setIsLoading] = useState(needsLoading); // Only true if initial load needs it
  const [loadingProgress, setLoadingProgress] = useState(
    needsLoading ? 0 : 100
  );
  const [loadingMessage, setLoadingMessage] = useState(
    needsLoading ? "Initializing..." : ""
  );

  // Callback to hide the React loader when its animation finishes
  const handleLoadingComplete = useCallback(() => {
    // console.log("AppLayout: Loading indicator onComplete triggered.");
    setIsLoading(false);
  }, []);

  // Effect to run the loading simulation *only* if needsLoading was true initially
  useEffect(() => {
    // Guard: Don't run loading sim if initial load wasn't for an app route
    if (!needsLoading) {
      // console.log("AppLayout: Skipping loading effect.");
      return;
    }

    // console.log("AppLayout: Running loading effect.");
    let isMounted = true;

    // Subscribe to progress updates from the manager
    const unsubscribe = loadingManager.subscribe(({ progress, status }) => {
      if (isMounted) {
        setLoadingProgress(progress);
        setLoadingMessage(status);
      }
    });

    // Simulate the loading process
    const startLoadingProcess = async () => {
      // Ensure manager starts at a low progress if this effect runs
      if (loadingManager.getState().progress < 5) {
        loadingManager.setProgress(5, "Initializing...");
      }
      try {
        // console.log("AppLayout: Starting loading steps...");
        // --- Define your loading steps here matching AppLoadingManager keys/weights ---
        // Make sure these steps exist in your AppLoadingManager configuration
        loadingManager.completeStep("app-init");
        await new Promise((res) => setTimeout(res, 300)); // Simulate work
        loadingManager.setStatus("Checking credentials...");
        loadingManager.completeStep("auth-check");
        await new Promise((res) => setTimeout(res, 400)); // Simulate work
        loadingManager.setStatus("Loading settings...");
        loadingManager.completeStep("settings-load");
        loadingManager.setStatus("Loading core components...");
        await new Promise((res) => setTimeout(res, 200)); // Simulate work
        loadingManager.completeStep("core-components");
        loadingManager.setStatus("Preparing interface...");
        await new Promise((res) => setTimeout(res, 100)); // Simulate work
        loadingManager.completeStep("ui-render");
        loadingManager.completeStep("lazy-components-ready"); // Ensure 100%

        // console.log("AppLayout: App loading process simulation complete.");
      } catch (error) {
        console.error("AppLayout: Error during initial loading steps:", error);
        if (isMounted) {
          // Update UI to show error state within the loader if possible
          loadingManager.setStatus("Failed to load application.");
          setLoadingMessage("Error loading application. Please refresh.");
          // Optionally force completion to hide loader after error:
          // setLoadingProgress(100);
          // setIsLoading(false); // Or maybe keep loader visible with error?
        }
      }
    };

    startLoadingProcess(); // Execute the loading steps

    // Cleanup function for the effect
    return () => {
      // console.log("AppLayout: Cleaning up loading effect.");
      isMounted = false;
      unsubscribe(); // Stop listening to the loading manager
      // Optional: Reset manager if needed when navigating away?
      // loadingManager.reset();
    };
    // Dependency array includes needsLoading to adhere to linting rules,
    // but the guard clause prevents execution if needsLoading was initially false.
  }, [needsLoading]);
  // --- Loading Logic END ---

  // --- Conditional Rendering ---

  // 1. Show React Loader: If the *initial* route needed loading AND the loading process is still active
  if (needsLoading && isLoading) {
    return (
      <AppLoadingIndicator
        progress={loadingProgress}
        status={loadingMessage}
        isVisible={isLoading} // Controls the component's internal visibility/animation state
        onComplete={handleLoadingComplete} // Called when fade-out completes
        minDisplayTime={1500} // Adjust minimum display time as needed
      />
    );
  }

  // 2. Show App Shell with Providers: If it's NOT an initial app load OR if loading is finished
  // console.log(
  //   `AppLayout: Rendering main structure (needsLoading: ${needsLoading}, isLoading: ${isLoading})`
  // );
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {/* NOTE: AuthProvider and ThemeProvider are now wrapping RouterProvider in App.jsx */}
      <SettingsProvider>
        {" "}
        {/* Keep SettingsProvider here */}
        <VersionProvider>
          {" "}
          {/* Keep VersionProvider here */}
          {/* Outlet renders the specific protected route component (e.g., Calendar, Settings) */}
          <Outlet />
        </VersionProvider>
      </SettingsProvider>
    </ErrorBoundary>
  );
};

export default AppLayout;
